/**
 * Setting up two-step sign-in before there is a session (W2.8b).
 *
 * Shown when the API answered `mfaEnrollmentRequired` with an enrolment
 * token instead of a session: on creating a workspace (option B, owner
 * decision 1 on Decision 89: the new workspace's owner sets up a second
 * factor first) and on signing in as someone whose role, or whose
 * workspace, requires one. The token is the only credential here; it names
 * the person and the workspace, so this screen sends nothing else.
 *
 *  1. `POST /v1/auth/mfa/enroll {enrollmentToken}` answers the setup key, an
 *     `otpauth://` link and ten recovery codes, once. They are shown here
 *     and nowhere else, ever.
 *  2. `POST /v1/auth/mfa/confirm {enrollmentToken, code}` with a code from
 *     the app answers the SESSION for that workspace (and sets its refresh
 *     cookie). `onEnrolled` takes it straight into the workspace: the person
 *     is never sent back to the sign-in page.
 *
 * A refusal is shown in the person's language with what to do; a start that
 * failed can be tried again. An expired or spent token (401) cannot be
 * retried here, so the sign-in link is offered instead.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Field, Input } from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { ApiError, post } from "@/lib/api/client";
import type { AtlasClaims } from "@/lib/auth/store";
import { useTranslation } from "@/lib/i18n";

/** `POST /v1/auth/mfa/enroll`'s answer, shown once. */
interface Enrolment {
  secret: string;
  provisioningUri: string;
  backupCodes: string[];
}

/** `POST /v1/auth/mfa/confirm`'s answer: enrolled, and the session it opened. */
export interface Enrolled {
  enrolled: true;
  accessToken: string;
  tenants: Array<{ code: string; name: string }>;
  claims: AtlasClaims;
  /** The workspace the session is for; "" for a person with none. */
  activeTenantCode: string;
}

/** An authenticator app's codes are six digits (RFC 6238 as Atlas uses it). */
const CODE_DIGITS = 6;

export function FactorEnrolment({
  enrollmentToken,
  onEnrolled,
}: {
  enrollmentToken: string;
  onEnrolled: (result: Enrolled) => void;
}) {
  const { t } = useTranslation();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // One start per token: StrictMode mounts twice, and every start replaces
  // the unconfirmed secret the person may already be typing from.
  const started = useRef<string | null>(null);

  const start = useCallback(() => {
    setBusy(true);
    setError(null);
    post<Enrolment>("/auth/mfa/enroll", { enrollmentToken })
      .then((result) => setEnrolment(result))
      .catch((err: unknown) => setError(err))
      .finally(() => setBusy(false));
  }, [enrollmentToken]);

  useEffect(() => {
    if (started.current === enrollmentToken) return;
    started.current = enrollmentToken;
    start();
  }, [enrollmentToken, start]);

  const confirm = (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    post<Enrolled>("/auth/mfa/confirm", {
      enrollmentToken,
      code: code.trim(),
    })
      .then((result) => onEnrolled(result))
      .catch((err: unknown) => {
        setError(err);
        setBusy(false);
      });
  };

  // A token that is no longer accepted (expired, spent, a new password) can
  // only be replaced by signing in again. At the confirm step the same 401
  // also answers a wrong code, so the form stays and the way out is offered
  // beside it: a token that ran out while the person was typing is never a
  // dead end.
  const refused =
    error instanceof ApiError &&
    error.status === 401 &&
    error.code === "AUTHENTICATION_FAILED";
  const tokenRefused = refused && enrolment === null;

  const failure =
    error !== null ? (
      <div
        role="alert"
        style={{ color: "var(--atlas-danger)", fontSize: 13, marginBlock: 8 }}
      >
        <ErrorText error={error} />
      </div>
    ) : null;

  return (
    <section aria-labelledby="factor-enrolment-title">
      <h2
        id="factor-enrolment-title"
        style={{ fontSize: 17, fontWeight: 600, marginBlock: "0 8px" }}
      >
        {t("mfaSetup.setupTitle")}
      </h2>
      <p style={{ fontSize: 13, marginBlock: "0 12px" }}>
        {t("errors.MFA_REQUIRED")}
      </p>
      {!enrolment ? (
        <div>
          {busy && (
            <p role="status" style={{ fontSize: 13 }}>
              {t("mfaSetup.starting")}
            </p>
          )}
          {failure}
          {!busy && error !== null && !tokenRefused && (
            <Button onClick={start}>{t("common.tryAgain")}</Button>
          )}
          {tokenRefused && (
            <Link to="/login" replace>
              {t("auth.mfa.backToSignIn")}
            </Link>
          )}
        </div>
      ) : (
        <form aria-label={t("mfaSetup.setupTitle")} onSubmit={confirm}>
          <p style={{ fontSize: 13, marginBlock: "0 4px" }}>
            {t("mfaSetup.step1")}
          </p>
          <div className="atlas-label">{t("mfaSetup.keyLabel")}</div>
          <p
            className="atlas-mono"
            data-testid="enrolment-secret"
            style={{ wordBreak: "break-all", marginBlock: "0 4px" }}
          >
            <bdi dir="ltr">{enrolment.secret}</bdi>
          </p>
          <p style={{ marginBlock: "0 12px" }}>
            <a href={enrolment.provisioningUri}>{t("mfaSetup.openApp")}</a>
          </p>
          <p style={{ fontSize: 13, marginBlock: "0 4px" }}>
            {t("mfaSetup.step2")}
          </p>
          <div className="atlas-label">{t("mfaSetup.recoveryCodes")}</div>
          <ul
            className="atlas-mono"
            dir="ltr"
            data-testid="enrolment-recovery-codes"
            style={{ marginBlock: "0 12px", paddingInlineStart: 20 }}
          >
            {enrolment.backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <p style={{ fontSize: 13, marginBlock: "0 4px" }}>
            {t("mfaSetup.step3", { digits: CODE_DIGITS })}
          </p>
          <Field label={t("mfaSetup.codeLabel")} required>
            <Input
              value={code}
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern={`[0-9]{${CODE_DIGITS}}`}
              maxLength={CODE_DIGITS}
              dir="ltr"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          {failure}
          {refused && (
            <p style={{ fontSize: 13, marginBlock: "0 8px" }}>
              <Link to="/login" replace>
                {t("auth.mfa.backToSignIn")}
              </Link>
            </p>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={busy || code.trim().length === 0}
            style={{ inlineSize: "100%" }}
          >
            {busy ? t("mfaSetup.checking") : t("mfaSetup.turnOn")}
          </Button>
        </form>
      )}
    </section>
  );
}
